import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/**
 * A custom hook that syncs state with localStorage
 * @param key - The localStorage key, or `null` to disable persistence entirely
 *   (behaves like a plain `useState` in that case — useful when a single
 *   component is reused in contexts that shouldn't share a preference)
 * @param initialValue - The initial value if no value exists in localStorage
 * @returns A stateful value and a function to update it, similar to useState
 */
export function useLocalStorageState<T>(
  key: string | null,
  initialValue: T,
): readonly [T, Dispatch<SetStateAction<T>>] {
  // Get initial state from localStorage or use initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!key) return initialValue;

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch {
      // If there's an error reading from localStorage, use initial value
      return initialValue;
    }
  });

  // Sync state changes to localStorage
  useEffect(() => {
    if (!key) return;

    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [key, storedValue]);

  return [storedValue, setStoredValue] as const;
}
